import React from 'react';

/**
 * Wspolne komponenty kontrolne dla zakladek Kasa-Bank:
 *  - DateRangeFilter - presety + Data od/Data do (server-side)
 *  - Pagination      - przyciski stronicowania + dropdown limit/strona
 *
 * Uzywane w AllOperationsTab.jsx oraz DocumentsTable.jsx (KP/KW).
 */

export function DateRangeFilter({ dateFilters, setDateFilters, label = 'Filtruj według daty:' }) {
  const presetClick = (preset) => () => {
    const today = new Date();
    const fmt = (d) => d.toISOString().slice(0, 10);
    let start = '', end = '';
    if (preset === 'today') {
      start = end = fmt(today);
    } else if (preset === 'yesterday') {
      const y = new Date(today); y.setDate(y.getDate() - 1);
      start = end = fmt(y);
    } else if (preset === 'thisWeek') {
      const day = today.getDay() || 7; // ndz=7
      const monday = new Date(today); monday.setDate(today.getDate() - (day - 1));
      start = fmt(monday); end = fmt(today);
    } else if (preset === 'thisMonth') {
      start = fmt(new Date(today.getFullYear(), today.getMonth(), 1));
      end = fmt(today);
    } else if (preset === 'prevMonth') {
      const first = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      const last = new Date(today.getFullYear(), today.getMonth(), 0);
      start = fmt(first); end = fmt(last);
    }
    setDateFilters({ startDate: start, endDate: end });
  };

  const presetBtn = {
    padding: '6px 12px', fontSize: '12px',
    border: '1px solid var(--border)',
    backgroundColor: 'var(--card-bg)', color: 'var(--text-secondary)',
    cursor: 'pointer', fontWeight: '500'
  };

  return (
    <div style={{
      marginBottom: '0.6rem', padding: '1rem',
      backgroundColor: 'var(--bg)', borderRadius: '0.5rem', border: '1px solid var(--border)'
    }}>
      <div style={{ marginBottom: '0.75rem', fontWeight: '600', color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
        <i className="fas fa-calendar-alt" style={{ marginRight: '0.5rem' }}></i>
        {label}
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', marginBottom: '0.75rem' }}>
        <button style={presetBtn} onClick={presetClick('today')}>Dzisiaj</button>
        <button style={presetBtn} onClick={presetClick('yesterday')}>Wczoraj</button>
        <button style={presetBtn} onClick={presetClick('thisWeek')}>Ten tydzień</button>
        <button style={presetBtn} onClick={presetClick('thisMonth')}>Ten miesiąc</button>
        <button style={presetBtn} onClick={presetClick('prevMonth')}>Poprzedni miesiąc</button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', alignItems: 'end' }}>
        <div>
          <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '11px', fontWeight: '500', color: 'var(--text-muted)' }}>
            Data od:
          </label>
          <input
            type="date"
            value={dateFilters.startDate}
            onChange={(e) => setDateFilters((prev) => ({ ...prev, startDate: e.target.value }))}
            style={{
              width: '100%', padding: '6px 10px', border: '1px solid var(--border)',
              borderRadius: '6px', fontSize: '12px', backgroundColor: 'var(--card-bg)', color: 'var(--text)'
            }}
          />
        </div>
        <div>
          <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '11px', fontWeight: '500', color: 'var(--text-muted)' }}>
            Data do:
          </label>
          <input
            type="date"
            value={dateFilters.endDate}
            onChange={(e) => setDateFilters((prev) => ({ ...prev, endDate: e.target.value }))}
            style={{
              width: '100%', padding: '6px 10px', border: '1px solid var(--border)',
              borderRadius: '6px', fontSize: '12px', backgroundColor: 'var(--card-bg)', color: 'var(--text)'
            }}
          />
        </div>
        <div>
          <button
            onClick={() => setDateFilters({ startDate: '', endDate: '' })}
            style={{
              padding: '6px 12px', fontSize: '12px', border: '1px solid var(--text-muted)',
              backgroundColor: 'var(--card-bg)', color: 'var(--text-muted)',
              cursor: 'pointer', fontWeight: '500', transition: 'all 0.2s ease'
            }}
          >
            <i className="fas fa-times" style={{ marginRight: '0.5rem' }}></i>
            Wyczyść daty
          </button>
        </div>
      </div>
    </div>
  );
}

export function Pagination({ page, pageLimit, totalCount, onPageChange, onLimitChange }) {
  const totalPages = Math.max(1, Math.ceil(totalCount / pageLimit));
  const safePage = Math.min(Math.max(1, page), totalPages);
  const from = totalCount === 0 ? 0 : (safePage - 1) * pageLimit + 1;
  const to = Math.min(safePage * pageLimit, totalCount);

  const btnStyle = (disabled) => ({
    padding: '6px 12px', fontSize: '12px',
    border: '1px solid var(--border)',
    backgroundColor: 'var(--card-bg)', color: disabled ? 'var(--text-muted)' : 'var(--text)',
    cursor: disabled ? 'not-allowed' : 'pointer', fontWeight: '500',
    opacity: disabled ? 0.5 : 1
  });

  return (
    <div style={{
      marginTop: '1rem', padding: '0.75rem 1rem',
      backgroundColor: 'var(--bg)', borderRadius: '0.5rem', border: '1px solid var(--border)',
      display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem'
    }}>
      <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
        {from}-{to} z {totalCount}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
        <button style={btnStyle(safePage <= 1)} disabled={safePage <= 1} onClick={() => onPageChange(1)}>«</button>
        <button style={btnStyle(safePage <= 1)} disabled={safePage <= 1} onClick={() => onPageChange(safePage - 1)}>‹ Poprzednia</button>
        <span style={{ fontSize: '0.8rem', color: 'var(--text)', padding: '0 0.5rem' }}>
          Strona {safePage} z {totalPages}
        </span>
        <button style={btnStyle(safePage >= totalPages)} disabled={safePage >= totalPages} onClick={() => onPageChange(safePage + 1)}>Następna ›</button>
        <button style={btnStyle(safePage >= totalPages)} disabled={safePage >= totalPages} onClick={() => onPageChange(totalPages)}>»</button>
        {onLimitChange && (
          <select
            value={pageLimit}
            onChange={(e) => onLimitChange(parseInt(e.target.value, 10))}
            style={{
              marginLeft: '0.5rem', padding: '6px 10px',
              border: '1px solid var(--border)', borderRadius: '6px',
              backgroundColor: 'var(--card-bg)', color: 'var(--text)',
              fontSize: '12px', cursor: 'pointer'
            }}
          >
            {[20, 50, 100, 200].map((n) => (
              <option key={n} value={n}>{n} / strona</option>
            ))}
          </select>
        )}
      </div>
    </div>
  );
}
